import { Component, computed, effect, inject, input, model, signal, untracked } from '@angular/core';
import { CommonModule } from '@angular/common';
import { DialogModule } from 'primeng/dialog';
import { TableModule } from 'primeng/table';
import { TagModule } from 'primeng/tag';
import { ButtonModule } from 'primeng/button';
import { finalize } from 'rxjs/operators';
import { AuditTrailManagementService } from '../../../management/services/audit-trail-management.service';
import { AuditTrail } from '../../models/audit_trail.model';

@Component({
  selector: 'app-asset-history-dialog',
  standalone: true,
  imports: [
    CommonModule,
    DialogModule,
    TableModule,
    TagModule,
    ButtonModule
  ],
  templateUrl: './asset-history-dialog.component.html',
  styleUrl: './asset-history-dialog.component.scss'
})
export class AssetHistoryDialogComponent {
  private auditTrailService = inject(AuditTrailManagementService);

  visible = model<boolean>(false);
  resourceName = input<string>('');
  recordId = input<number | string | null>(null);
  recordLabel = input<string>('');

  loading = signal(false);
  error = signal<string | null>(null);
  trails = signal<AuditTrail[]>([]);
  totalRecords = signal(0);
  first = signal(0);
  rows = signal(10);

  headerTitle = computed(() => {
    const label = this.recordLabel()?.trim();
    const id = this.recordId();
    const suffix = label || (id != null ? String(id) : '');
    return suffix ? `History — ${suffix}` : 'History';
  });

  private exactPhrase = computed(() => {
    const id = this.recordId();
    const resource = this.resourceName()?.trim();
    if (id == null || id === '' || !resource) {
      return '';
    }
    return `record ${id} in ${resource}`;
  });

  constructor() {
    effect(() => {
      const open = this.visible();
      const phrase = this.exactPhrase();
      if (open && phrase) {
        untracked(() => {
          this.first.set(0);
          this.loadPage(1, this.rows());
        });
      } else if (!open) {
        untracked(() => {
          this.trails.set([]);
          this.totalRecords.set(0);
          this.error.set(null);
          this.first.set(0);
        });
      }
    });
  }

  onPage(event: { first: number; rows: number }): void {
    this.first.set(event.first);
    this.rows.set(event.rows);
    const page = Math.floor(event.first / event.rows) + 1;
    this.loadPage(page, event.rows);
  }

  actionSeverity(action: string): 'success' | 'info' | 'warn' | 'danger' | 'secondary' | 'contrast' {
    const normalized = (action || '').toLowerCase();
    if (normalized.includes('insert') || normalized.includes('create')) {
      return 'success';
    }
    if (normalized.includes('update')) {
      return 'info';
    }
    if (normalized.includes('delete')) {
      return 'danger';
    }
    if (normalized.includes('view')) {
      return 'secondary';
    }
    if (normalized.includes('login')) {
      return 'warn';
    }
    return 'contrast';
  }

  private loadPage(page: number, size: number): void {
    const phrase = this.exactPhrase();
    if (!phrase) {
      this.trails.set([]);
      this.totalRecords.set(0);
      return;
    }

    this.loading.set(true);
    this.error.set(null);

    this.auditTrailService
      .findAuditTrails(page, size, phrase, undefined, 'Login Attempt')
      .pipe(finalize(() => this.loading.set(false)))
      .subscribe({
        next: (result) => {
          const exact = this.filterExactMatches(result.data || []);
          this.trails.set(exact);
          const serverData = result.data || [];
          if (serverData.length > 0 && exact.length === serverData.length) {
            this.totalRecords.set(result.total ?? exact.length);
          } else {
            this.totalRecords.set(exact.length);
          }
        },
        error: () => {
          this.error.set('Failed to load audit history');
          this.trails.set([]);
          this.totalRecords.set(0);
        }
      });
  }

  /**
   * Exact match for "record {id} in {resource}" so "record 1" does not match "record 10".
   */
  private filterExactMatches(items: AuditTrail[]): AuditTrail[] {
    const id = this.recordId();
    const resource = this.resourceName()?.trim();
    if (id == null || !resource) {
      return [];
    }
    const strict = new RegExp(
      `(?:^|\\s)record\\s+${this.escapeRegExp(String(id))}\\s+in\\s+${this.escapeRegExp(resource)}(?:\\s|$|[.,;:!?)]|$)`,
      'i'
    );
    return items.filter((item) => strict.test(item.description || ''));
  }

  private escapeRegExp(value: string): string {
    return value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  }
}
